import { ListAlt } from '@mui/icons-material'
import { Box, CircularProgress, Fab, Stack, SwipeableDrawer, Typography } from '@mui/material'
import type { ContentAST } from '@shared/ast/astTypes'
import { getEncryptionIds } from '@shared/ast/astUtils'
import {
  formatChapterSectionHeadingForSidebarDisplay,
  isChapterSectionHeadingText,
  stripChapterSectionHeadingPrefix,
} from '@shared/ast/tocChapterSectionHeading'
import { type Chapter, flattenChaptersToSectionKeys } from '@shared/chapterSectionsOrder'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { Link, useParams } from 'react-router-dom'

import ContentLanguagePicker from '../components/ContentLanguagePicker'
import DecryptionModal from '../components/decryption/DecryptionModal'
import { LexicalEditor } from '../components/editor/lexical'
import CharacterConsentMenu, { type ConsentAction } from '../components/encryption/CharacterConsentMenu'
import { useAuth } from '../contexts/AuthContext'
import { useBodyScrollLock } from '../hooks/useBodyScrollLock'
import { useReaderLightPalette } from '../hooks/useReaderLightPalette'
import { Book, ContentFile, apiService } from '../services/api'
import { READER_PAGE_BACKGROUND, READER_PAPER_BACKGROUND_SX } from '../styles/paperBackground'
import { HeadingBlock, getBlockText } from '../transforms/ast'
import { collectEncryptedGlyphs, collectSealedEncryptionIds } from '../utils/collectEncryptedGlyphs'
import { formatChapterLabel } from '../utils/hebrewNumeral'
import { logger } from '../utils/logger'

/**
 * Identify a rendered reader chapter uniquely.
 * Reader chapters may be per-section slices of a source file (Phase D+), in which case
 * multiple entries share a filename and only (filename, sectionId) is unique.
 */
function chapterKey(chapter: ContentFile | { filename: string; sectionId?: string }): string {
  return chapter.sectionId ? `${chapter.filename}__${chapter.sectionId}` : chapter.filename
}

/**
 * Sort reader chapters into the reading order defined by chapter-section definitions.
 *
 * Each reader chapter carries a `sectionId` (per-section slice of a source file) that
 * resolves to a `sourceFilename:blockId` key into the chapter definitions. Entries
 * without a match keep their order.
 */
function sortChaptersByDefinitions(chapters: ContentFile[], chapterDefs: Chapter[] | undefined | null): ContentFile[] {
  if (!chapterDefs?.length) {
    return chapters
  }

  const flatKeys = flattenChaptersToSectionKeys(chapterDefs)
  if (flatKeys.length === 0) {
    return chapters
  }

  const positionOf = new Map<string, number>()
  for (let i = 0; i < flatKeys.length; i++) {
    positionOf.set(flatKeys[i], i)
  }

  const withPos = chapters.map((ch, originalIdx) => {
    if (ch.sectionId) {
      const key = `${ch.filename}:${ch.sectionId}`
      const pos = positionOf.get(key)
      if (pos !== undefined) {
        return { ch, pos, originalIdx }
      }
    }
    let earliest = Infinity
    for (const [key, pos] of positionOf) {
      const colonIdx = key.indexOf(':')
      if (colonIdx > 0 && key.slice(0, colonIdx) === ch.filename) {
        earliest = Math.min(earliest, pos)
      }
    }
    if (earliest !== Infinity) {
      return { ch, pos: earliest - 0.5, originalIdx }
    }
    return { ch, pos: Infinity, originalIdx }
  })

  return withPos.sort((a, b) => a.pos - b.pos || a.originalIdx - b.originalIdx).map((item) => item.ch)
}

/**
 * Reorder AST blocks so that sections (delimited by heading blocks whose ids
 * appear in `orderedHeadingBlockIds`) follow the given order instead of their
 * original document position. Preamble content stays at the top.
 */
function reorderAstSections(ast: ContentAST, orderedHeadingBlockIds: string[]): ContentAST {
  if (orderedHeadingBlockIds.length <= 1) {
    return ast
  }

  const sectionIdSet = new Set(orderedHeadingBlockIds)

  const sectionStarts: { blockId: string; index: number }[] = []
  for (let i = 0; i < ast.blocks.length; i++) {
    if (ast.blocks[i].type === 'heading' && sectionIdSet.has(ast.blocks[i].id)) {
      sectionStarts.push({ blockId: ast.blocks[i].id, index: i })
    }
  }

  if (sectionStarts.length <= 1) {
    return ast
  }

  const desiredOrder = orderedHeadingBlockIds.filter((id) => sectionStarts.some((s) => s.blockId === id))

  const currentOrder = sectionStarts.map((s) => s.blockId)
  if (currentOrder.every((id, i) => desiredOrder[i] === id)) {
    return ast
  }

  const firstIdx = sectionStarts[0].index
  const preamble = ast.blocks.slice(0, firstIdx)

  const sections = new Map<string, typeof ast.blocks>()
  for (let s = 0; s < sectionStarts.length; s++) {
    const start = sectionStarts[s].index
    const end = s + 1 < sectionStarts.length ? sectionStarts[s + 1].index : ast.blocks.length
    sections.set(sectionStarts[s].blockId, ast.blocks.slice(start, end))
  }

  const reordered = [...preamble]
  const placed = new Set<string>()
  for (const blockId of desiredOrder) {
    const sectionBlocks = sections.get(blockId)
    if (sectionBlocks) {
      reordered.push(...sectionBlocks)
      placed.add(blockId)
    }
  }
  for (const { blockId } of sectionStarts) {
    if (!placed.has(blockId)) {
      const sectionBlocks = sections.get(blockId)
      if (sectionBlocks) {
        reordered.push(...sectionBlocks)
      }
    }
  }

  return { ...ast, blocks: reordered }
}

function extractSectionBlockIdsForFile(filename: string, chapterDefs: Chapter[] | undefined | null): string[] {
  if (!chapterDefs?.length) {
    return []
  }
  const blockIds: string[] = []
  for (const ch of chapterDefs) {
    for (const key of ch.chapterSections) {
      const colonIdx = key.indexOf(':')
      if (colonIdx <= 0) {
        continue
      }
      if (key.slice(0, colonIdx) === filename) {
        blockIds.push(key.slice(colonIdx + 1))
      }
    }
  }
  return blockIds
}

type ReaderTocRow = { chapter: ContentFile; displayText: string; blockId: string }
type ReaderTocGroup = { chapterDef: Chapter | null; rows: ReaderTocRow[] }

function collectReaderChapterSectionsToc(chapters: ContentFile[], chapterDefs?: Chapter[] | null): ReaderTocGroup[] {
  // Collect TOC rows from each chapter (server-provided or AST-scanned)
  const rows: ReaderTocRow[] = []
  for (const ch of chapters) {
    const fromServer = ch.readerTocSections
    if (fromServer?.length) {
      for (const tocEntry of fromServer) {
        rows.push({ chapter: ch, displayText: tocEntry.displayText, blockId: tocEntry.blockId })
      }
      continue
    }
    const ast = ch.content
    if (!ast) {
      continue
    }
    for (const block of ast.blocks) {
      if (block.type !== 'heading') {
        continue
      }
      const heading = block as HeadingBlock
      const text = heading.content
        .filter((node) => node.type === 'text')
        .map((node) => (node as { text: string }).text)
        .join('')
      if (!isChapterSectionHeadingText(text)) {
        continue
      }
      const afterCaret = stripChapterSectionHeadingPrefix(text)
      const displayText = formatChapterSectionHeadingForSidebarDisplay(afterCaret).trim() || afterCaret.trim() || text
      rows.push({ chapter: ch, displayText, blockId: heading.id })
    }
  }

  if (rows.length === 0) {
    return []
  }

  // Group rows by chapterId (set by server in reader response)
  if (chapterDefs?.length) {
    const defById = new Map(chapterDefs.map((d) => [d.id, d]))
    const groupMap = new Map<string, ReaderTocRow[]>()
    const ungrouped: ReaderTocRow[] = []
    for (const row of rows) {
      const cid = row.chapter.chapterId
      if (cid && defById.has(cid)) {
        const list = groupMap.get(cid) ?? []
        list.push(row)
        groupMap.set(cid, list)
      } else {
        ungrouped.push(row)
      }
    }
    const groups: ReaderTocGroup[] = chapterDefs.map((def) => ({
      chapterDef: def,
      rows: groupMap.get(def.id) ?? [],
    }))
    if (ungrouped.length > 0) {
      groups.push({ chapterDef: null, rows: ungrouped })
    }
    return groups
  }

  return [{ chapterDef: null, rows }]
}

function normalizeReaderSidebarHeadingLabel(s: string): string {
  const t = formatChapterSectionHeadingForSidebarDisplay(s.trim()).trim() || s.trim()
  const noEnum = t.replace(/^\d+\.\s*/, '').trim()
  return noEnum.normalize('NFC').replace(/\s+/g, ' ')
}

/**
 * Reader sidebar lists `chapter.title` then nested headings. Chapter-export slices repeat the same line; other chapters
 * can too when the title matches the first heading. Only the active chapter expands nested lines.
 */
function readerSidebarHeadingsDedupeLeadingTitleDuplicate(
  chapter: ContentFile,
  rows: Array<{
    text: string
    level: number
    id: string
    isTocSection: boolean
    displayText: string
  }>,
) {
  if (rows.length === 0) {
    return rows
  }
  const first = rows[0]
  const a = normalizeReaderSidebarHeadingLabel(chapter.title)
  const b = normalizeReaderSidebarHeadingLabel(first.displayText)
  if (a.length > 0 && b.length > 0 && a === b) {
    return rows.slice(1)
  }
  return rows
}

// -- Decorative SVG ornaments for the Little Prince aesthetic --

/** Delicate floral/vine ornament used as chapter divider */
const ChapterOrnament: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    width="120"
    height="24"
    viewBox="0 0 120 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ opacity: 0.4 }}
  >
    {/* Center diamond */}
    <path d="M60 4 L66 12 L60 20 L54 12 Z" fill="var(--ornament-fill)" />
    {/* Left vine */}
    <path
      d="M50 12 Q40 6 28 12 Q16 18 4 12"
      stroke="var(--ornament-stroke)"
      strokeWidth="1.2"
      fill="none"
      strokeLinecap="round"
    />
    {/* Right vine */}
    <path
      d="M70 12 Q80 6 92 12 Q104 18 116 12"
      stroke="var(--ornament-stroke)"
      strokeWidth="1.2"
      fill="none"
      strokeLinecap="round"
    />
    {/* Left leaves */}
    <ellipse
      cx="28"
      cy="10"
      rx="3"
      ry="1.5"
      fill="var(--ornament-stroke)"
      transform="rotate(-30 28 10)"
      opacity="0.6"
    />
    <ellipse cx="16" cy="15" rx="3" ry="1.5" fill="var(--ornament-stroke)" transform="rotate(20 16 15)" opacity="0.5" />
    {/* Right leaves */}
    <ellipse cx="92" cy="10" rx="3" ry="1.5" fill="var(--ornament-stroke)" transform="rotate(30 92 10)" opacity="0.6" />
    <ellipse
      cx="104"
      cy="15"
      rx="3"
      ry="1.5"
      fill="var(--ornament-stroke)"
      transform="rotate(-20 104 15)"
      opacity="0.5"
    />
  </svg>
)

/** Young sapling — fuller leaves angled 45° toward the sky. */
const SaplingGlyph: React.FC<{ size?: number }> = ({ size = 72 }) => (
  <svg width={size} height={size} viewBox="0 0 72 72" fill="none" xmlns="http://www.w3.org/2000/svg">
    {/* ground hint */}
    <path
      d="M14 56 Q36 52 58 56"
      stroke="var(--ornament-stroke)"
      strokeWidth="0.7"
      fill="none"
      strokeLinecap="round"
      opacity="0.4"
    />
    {/* curved stem */}
    <path
      d="M36 56 C 35 48, 37 40, 36 28"
      stroke="var(--ornament-stroke)"
      strokeWidth="1"
      fill="none"
      strokeLinecap="round"
    />
    {/* left leaf — 45° up-left */}
    <path
      d="M36 40 Q24 36 20 24 Q32 28 36 40 Z"
      fill="var(--ornament-fill)"
      fillOpacity="0.4"
      stroke="var(--ornament-stroke)"
      strokeWidth="0.75"
      strokeLinejoin="round"
    />
    {/* right leaf — 45° up-right */}
    <path
      d="M36 34 Q48 30 52 18 Q40 22 36 34 Z"
      fill="var(--ornament-fill)"
      fillOpacity="0.4"
      stroke="var(--ornament-stroke)"
      strokeWidth="0.75"
      strokeLinejoin="round"
    />
    {/* tip bud */}
    <circle cx="36" cy="28" r="1.3" fill="var(--ornament-stroke)" />
  </svg>
)

/**
 * Placeholder shown where unpublished chapter content will eventually appear.
 * Static sapling glyph — signals "not yet written" (growth to come), not "loading".
 * Chapter title renders inside the frame so empty chapters are self-labeled.
 */
const ChapterPlaceholder: React.FC<{ id?: string; titleFont?: string; chapterNumber?: string }> = ({
  id,
  titleFont,
  chapterNumber,
}) => (
  <Box
    id={id}
    sx={{
      minHeight: { xs: '32vh', md: '42vh' },
      my: { xs: 4, md: 6 },
      mx: { xs: 2, md: 4 },
      borderRadius: 2,
      border: '1px solid rgba(255, 255, 255, 0.2)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      scrollMarginTop: '80px',
    }}
  >
    {chapterNumber !== undefined && (
      <Typography
        sx={{
          fontFamily: titleFont,
          fontSize: { xs: '1.4rem', md: '1.6rem' },
          fontWeight: 300,
          color: 'var(--reader-text-secondary)',
          letterSpacing: '0.05em',
          opacity: 0.85,
          pt: { xs: 3.5, md: 5 },
          pb: { xs: 1, md: 1.5 },
        }}
      >
        {chapterNumber}
      </Typography>
    )}
    <Box
      sx={{
        flex: 1,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        pb: { xs: 3, md: 4 },
        opacity: 0.6,
      }}
    >
      <Box
        sx={{
          width: { xs: 110, md: 140 },
          height: { xs: 110, md: 140 },
          borderRadius: '50%',
          background: 'radial-gradient(ellipse at center, var(--reader-star-glow) 0%, transparent 70%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <SaplingGlyph size={64} />
      </Box>
    </Box>
  </Box>
)

/** Small between-sections ornament: dot · diamond · dot (within-chapter divider) */
const SectionOrnament: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      my: { xs: 3, md: 4 },
      userSelect: 'none',
      pointerEvents: 'none',
    }}
  >
    <svg
      width="72"
      height="14"
      viewBox="0 0 72 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ opacity: 0.7 }}
    >
      <circle cx="18" cy="7" r="1.4" fill="var(--ornament-stroke)" />
      <path d="M36 2 L41 7 L36 12 L31 7 Z" fill="var(--ornament-fill)" />
      <circle cx="54" cy="7" r="1.4" fill="var(--ornament-stroke)" />
    </svg>
  </Box>
)

/**
 * Gap indicator for flat book structure: a "meadow" of saplings of varied
 * heights, scattered horizontally with generous vertical padding. Reuses the
 * book's existing sapling motif (which already reads as "growth not yet
 * revealed" via `ChapterPlaceholder`) but as a *field* rather than a single
 * placeholder — many things growing here, indeterminate count, more on their
 * own timelines. Rendered before a section whose immediate predecessor in
 * `flatSectionsOrder` is unpublished.
 */
const FlatGapMeadow: React.FC = () => {
  // Varied heights are deliberate — communicates "different stages, no fixed
  // count." Keep the list odd so the centre sapling is the tallest, giving
  // the meadow a soft natural curve from the edges inward.
  const saplingSizes = [40, 56, 48, 64, 44, 60, 42]
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'space-around',
        flexWrap: 'nowrap',
        px: { xs: 1, md: 3 },
        py: { xs: 9, md: 13 },
        userSelect: 'none',
        pointerEvents: 'none',
        opacity: 0.55,
      }}
    >
      {saplingSizes.map((size, i) => (
        <Box
          key={i}
          sx={{
            opacity: 0.7 + ((i * 37) % 10) / 33,
            display: 'flex',
            alignItems: 'flex-end',
          }}
        >
          <SaplingGlyph size={size} />
        </Box>
      ))}
    </Box>
  )
}

/** Small star cluster — like The Little Prince's stars */
const StarCluster: React.FC<{ size?: number }> = ({ size = 40 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 40 40"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ opacity: 0.35 }}
  >
    <circle cx="20" cy="8" r="1.5" fill="var(--ornament-fill)" />
    <circle cx="12" cy="20" r="1" fill="var(--ornament-stroke)" />
    <circle cx="28" cy="18" r="1.2" fill="var(--ornament-fill)" />
    <circle cx="18" cy="30" r="0.8" fill="var(--ornament-stroke)" />
    <circle cx="30" cy="28" r="1" fill="var(--ornament-fill)" />
  </svg>
)

const TheLastManifesto: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth()
  // Mounted both at `/` (the home page's book) and at `/read/:bookId[/:chapterId]`,
  // which names a book and optionally the entry to open on.
  const { bookId: routeBookId, chapterId: routeChapterId } = useParams<{ bookId?: string; chapterId?: string }>()

  // Pin the reader chrome by making the inner content the only scrollable surface (see hook).
  useBodyScrollLock()
  useReaderLightPalette()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [book, setBook] = useState<Book | null>(null)
  const [chapters, setChapters] = useState<ContentFile[]>([])
  const [characters, setCharacters] = useState<Array<{ tagId: string; name: string }>>([])
  const [activeChapterId, setActiveChapterId] = useState<string | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Language state
  const [availableLanguages, setAvailableLanguages] = useState<string[]>([])
  const [contentLang, setContentLang] = useState<string>(() => {
    const stored = localStorage.getItem('contentLanguage')
    if (stored) {
      return stored
    }
    const browserLang = navigator.language.split('-')[0]
    return browserLang || 'he'
  })

  // Mobile TOC bottom sheet
  const [tocOpen, setTocOpen] = useState(false)

  // Bumped to re-pull the bundle after the viewer changes what may be read of theirs.
  const [reloadNonce, setReloadNonce] = useState(0)

  // The character's own choice about one of their passages.
  const [consentTarget, setConsentTarget] = useState<{ encryptionId: string; anchor: HTMLElement } | null>(null)

  // Decryption modal state
  const [decryptionModalOpen, setDecryptionModalOpen] = useState(false)
  const [encryptedContentToDecrypt, setEncryptedContentToDecrypt] = useState('')
  const [currentEncryptionId, setCurrentEncryptionId] = useState('')
  const [mnemonicMap, setMnemonicMap] = useState<Record<string, string>>({})

  const isAuthor = Boolean(book && user && !authLoading && book.author._id === user.id)

  const handleLanguageChange = useCallback((lang: string) => {
    setContentLang(lang)
    localStorage.setItem('contentLanguage', lang)
  }, [])

  const effectiveLang = availableLanguages.includes(contentLang) ? contentLang : 'he'

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const langParam = effectiveLang !== 'he' ? effectiveLang : undefined
        const response = await apiService.getTheLastManifestoReader(langParam, routeBookId)

        if (!response.success || !response.data) {
          setError('The Last Manifesto book could not be found.')
          return
        }

        const { book: bookData, chapters: chaptersWithContent, languages } = response.data
        setBook(bookData)
        setAvailableLanguages(languages.available)
        setChapters(chaptersWithContent)
        setCharacters(response.data.characters ?? [])

        if (chaptersWithContent.length > 0) {
          setActiveChapterId(chapterKey(chaptersWithContent[0]))
        }
      } catch (err: any) {
        logger.error('Error fetching The Last Manifesto:', err)
        setError(err.message || 'Failed to load The Last Manifesto. Please try again later.')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [effectiveLang, routeBookId, reloadNonce])

  const isFlat = book?.settings?.structure === 'flat'

  const chaptersInReadingOrder = useMemo(() => {
    // Flat structure: the server already returns chapters sorted by
    // `flatSectionsOrder` (and tags the last entry with `hasGapBefore` if a
    // pre-publishing gap exists). Re-sorting by chapter definitions would
    // undo that — so just pass them through.
    if (isFlat) {
      return chapters
    }
    const chapterDefs = book?.settings?.chapters as Chapter[] | undefined
    return sortChaptersByDefinitions(chapters, chapterDefs)
  }, [chapters, book?.settings?.chapters, isFlat])

  // Track active chapter on scroll
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container || chaptersInReadingOrder.length === 0) {
      return
    }

    const handleScroll = () => {
      const containerTop = container.scrollTop + 200
      let currentChapter = chaptersInReadingOrder[0] ? chapterKey(chaptersInReadingOrder[0]) : undefined

      for (const chapter of chaptersInReadingOrder) {
        const key = chapterKey(chapter)
        const el = document.getElementById(`chapter-${key}`)
        if (el && el.offsetTop <= containerTop) {
          currentChapter = key
        }
      }

      if (currentChapter && currentChapter !== activeChapterId) {
        setActiveChapterId(currentChapter)
      }
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [chaptersInReadingOrder, activeChapterId])

  const handleChapterClick = useCallback((chapterId: string) => {
    const chapterElement = document.getElementById(`chapter-${chapterId}`)
    if (chapterElement) {
      chapterElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      setActiveChapterId(chapterId)
    }
  }, [])

  const readerPinnedTocGroups = useMemo(
    () =>
      isFlat
        ? collectReaderChapterSectionsToc(chapters, undefined)
        : collectReaderChapterSectionsToc(chapters, book?.settings?.chapters as Chapter[] | undefined),
    [chapters, book?.settings?.chapters, isFlat],
  )
  const readerSidebarPinsOnly = readerPinnedTocGroups.length > 0

  const reorderedContentByKey = useMemo(() => {
    const chapterDefs = book?.settings?.chapters as Chapter[] | undefined
    if (!chapterDefs?.length) {
      return null
    }
    const map = new Map<string, ContentAST>()
    for (const chapter of chapters) {
      if (!chapter.content || chapter.sectionId) {
        continue
      }
      const sectionOrder = extractSectionBlockIdsForFile(chapter.filename, chapterDefs)
      if (sectionOrder.length > 1) {
        const reordered = reorderAstSections(chapter.content, sectionOrder)
        if (reordered !== chapter.content) {
          map.set(chapterKey(chapter), reordered)
        }
      }
    }
    return map.size > 0 ? map : null
  }, [chapters, book?.settings?.chapters])

  /**
   * Open on the entry the URL names. `/read/:bookId/:chapterId` carries a source
   * filename, which can hold several published entries — the first one is the chapter's
   * opening, which is what a link to it means. A full `filename__blockId` key also works,
   * so a link to one particular section lands on it.
   */
  const deepLinkedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!routeChapterId || loading || chaptersInReadingOrder.length === 0) {
      return
    }
    if (deepLinkedRef.current === routeChapterId) {
      return
    }
    const target =
      chaptersInReadingOrder.find((c) => chapterKey(c) === routeChapterId) ??
      chaptersInReadingOrder.find((c) => c.filename === routeChapterId)
    if (!target) {
      return
    }
    deepLinkedRef.current = routeChapterId
    const key = chapterKey(target)
    setActiveChapterId(key)
    // One frame after the entries paint, or the element is not there to scroll to.
    window.requestAnimationFrame(() => {
      document.getElementById(`chapter-${key}`)?.scrollIntoView({ behavior: 'auto', block: 'start' })
    })
  }, [routeChapterId, loading, chaptersInReadingOrder])

  const handlePinnedHeadingClick = useCallback((chapterKeyStr: string, blockId: string) => {
    setActiveChapterId(chapterKeyStr)
    const chapterElement = document.getElementById(`chapter-${chapterKeyStr}`)
    if (chapterElement) {
      chapterElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    window.setTimeout(() => {
      const el = document.querySelector(`#chapter-${chapterKeyStr} [data-block-id="${blockId}"]`)
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 120)
  }, [])

  /** The characters live at the end of the book now, so the contents scrolls rather than leaves. */
  const scrollToCharacters = useCallback(() => {
    document.getElementById('characters-appendix')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  const handleChapterHeaderClick = useCallback((chapterDefId: string) => {
    const el = document.getElementById(`chapter-def-${chapterDefId}`)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  /**
   * Which source file an encrypted passage lives in. The consent route addresses a
   * passage by (file, encryption id), and this page renders every entry at once — so
   * the file has to be looked up rather than read off a "current chapter".
   */
  const filenameByEncryptionId = useMemo(() => {
    const map = new Map<string, string>()
    for (const chapter of chaptersInReadingOrder) {
      const content = reorderedContentByKey?.get(chapterKey(chapter)) ?? chapter.content
      if (!content) {
        continue
      }
      for (const id of getEncryptionIds(content)) {
        map.set(id, chapter.filename)
      }
    }
    return map
  }, [chaptersInReadingOrder, reorderedContentByKey])

  const handleConsentRequest = useCallback((encryptionId: string, anchor: HTMLElement) => {
    setConsentTarget({ encryptionId, anchor })
  }, [])

  const handleCharacterConsent = useCallback(
    async (action: ConsentAction) => {
      const encryptionId = consentTarget?.encryptionId
      const filename = encryptionId ? filenameByEncryptionId.get(encryptionId) : undefined
      if (!book || !encryptionId || !filename) {
        return
      }
      try {
        await apiService.setCharacterConsent(book._id, filename, encryptionId, action)
        toast.success('הקטע נפתח לכולם')
        setReloadNonce((n) => n + 1)
      } catch (err: any) {
        toast.error(err?.message || 'לא הצלחנו לשמור את הבחירה')
      }
    },
    [book, consentTarget, filenameByEncryptionId],
  )

  const handleEncryptedClick = useCallback((encryptedText: string, encryptionId: string) => {
    setEncryptedContentToDecrypt(encryptedText)
    setCurrentEncryptionId(encryptionId)
    setDecryptionModalOpen(true)
  }, [])

  const handleDecryptSuccess = useCallback(
    (decryptedText: string, mnemonic: string) => {
      setMnemonicMap((prev) => ({
        ...prev,
        [currentEncryptionId]: mnemonic,
      }))
    },
    [currentEncryptionId],
  )

  const getChapterHeadings = (chapter: ContentFile) => {
    const ast = chapter.content
    if (!ast) {
      return []
    }

    const cleanBlocks = ast.blocks
    const rows = cleanBlocks
      .filter((block) => block.type === 'heading')
      .map((block) => {
        const heading = block as HeadingBlock
        const text = getBlockText(heading)
        return {
          text,
          level: heading.level,
          id: heading.id,
          isTocSection: isChapterSectionHeadingText(text),
          displayText: stripChapterSectionHeadingPrefix(text).trim() || text,
        }
      })
    return readerSidebarHeadingsDedupeLeadingTitleDuplicate(chapter, rows)
  }

  const isRtl = ['he', 'ar', 'fa'].includes(effectiveLang)

  /**
   * Sealed passages across every chapter on the page. Encryption ids are unique
   * book-wide, so one set serves all of them — and building it once keeps the reader
   * from allocating a fresh Set per chapter on every render.
   */
  const sealedIds = useMemo(() => {
    const out = new Set<string>()
    for (const chapter of chaptersInReadingOrder) {
      const content = reorderedContentByKey?.get(chapterKey(chapter)) ?? chapter.content
      for (const id of collectSealedEncryptionIds(content)) {
        out.add(id)
      }
    }
    return out
  }, [chaptersInReadingOrder, reorderedContentByKey])

  /**
   * Ciphertext for passages this viewer may read — their own, or ones already opened
   * to everyone. Present only on their copy of the bundle, and it is what the text ↔
   * glyphs toggle flips to.
   */
  const glyphMap = useMemo(() => {
    const out: Record<string, string> = {}
    for (const chapter of chaptersInReadingOrder) {
      const content = reorderedContentByKey?.get(chapterKey(chapter)) ?? chapter.content
      Object.assign(out, collectEncryptedGlyphs(content))
    }
    return out
  }, [chaptersInReadingOrder, reorderedContentByKey])
  const bookFont = book?.settings.defaultFontFamily || "'SBL Hebrew', 'Times New Roman', serif"
  const bookTitleFont = book?.settings.titleFontFamily || "'SBL Hebrew', 'Times New Roman', serif"

  // -- Loading state --
  if (loading || authLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 3,
          bgcolor: READER_PAGE_BACKGROUND,
        }}
      >
        <StarCluster size={60} />
        <CircularProgress size={32} sx={{ color: 'var(--reader-spinner)' }} />
      </Box>
    )
  }

  // -- Error state --
  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 3,
          bgcolor: READER_PAGE_BACKGROUND,
          px: 4,
        }}
      >
        <StarCluster size={50} />
        <Typography
          sx={{
            fontFamily: bookFont,
            color: 'var(--reader-text-muted)',
            fontSize: '1.1rem',
            textAlign: 'center',
            maxWidth: 400,
          }}
        >
          {error}
        </Typography>
      </Box>
    )
  }

  return (
    <>
      <Box
        sx={{
          // Dynamic viewport height so the shell tracks the visible area as the mobile URL bar
          // shows/hides. Static 100vh overflows the visual viewport while the URL bar is shown,
          // producing a body-level scroll that drags pinned chrome off-screen.
          height: '100dvh',
          display: 'flex',
          overflow: 'hidden',
          bgcolor: READER_PAGE_BACKGROUND,
        }}
        dir={isRtl ? 'rtl' : 'ltr'}
      >
        {/* ═══════════════ Sidebar — Book Table of Contents ═══════════════ */}
        <Box
          sx={{
            width: { xs: '100%', md: 260 },
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            // Deep muted sage — the book-cover companion to warm cream paper.
            bgcolor: 'var(--reader-sidebar-bg)',
            position: 'relative',
            borderRight: 'none',
            // Locally override reader text/UI tokens so the TOC reads as
            // cream-on-binding instead of brown-on-brown. Scoped to this Box —
            // the main reading area keeps the global warm-brown text tokens.
            '--reader-text-primary': '#f4ead5',
            '--reader-text-secondary': '#e2d6bb',
            '--reader-text-muted': '#c0c8b5',
            '--reader-text-submuted': '#a4ad9d',
            '--reader-heading-active': '#f8efd9',
            '--reader-heading-inactive': '#c0c8b5',
            '--reader-sidebar-divider': 'rgba(244, 234, 213, 0.18)',
            '--reader-hover': 'rgba(244, 234, 213, 0.08)',
            '--reader-hover-sub': 'rgba(244, 234, 213, 0.05)',
            '--reader-scrollbar-main': 'rgba(244, 234, 213, 0.18)',
            '--reader-scrollbar-track': 'rgba(244, 234, 213, 0.04)',
          }}
        >
          {/* Book title in sidebar */}
          <Box sx={{ pt: 5, pb: 3, px: 3 }}>
            <Typography
              sx={{
                fontFamily: bookTitleFont,
                fontWeight: 'normal',
                fontSize: '1.15rem',
                color: 'var(--reader-text-secondary)',
                textAlign: isRtl ? 'right' : 'left',
                lineHeight: 1.6,
                letterSpacing: '0.02em',
              }}
            >
              {book?.title}
            </Typography>
            {availableLanguages.length > 1 && (
              <Box sx={{ mt: 1.5 }}>
                <ContentLanguagePicker
                  languages={availableLanguages}
                  currentLang={effectiveLang}
                  onChange={handleLanguageChange}
                />
              </Box>
            )}
          </Box>

          {/* Subtle divider line */}
          <Box
            sx={{
              mx: 3,
              mb: 2,
              borderBottom: '1px solid var(--reader-sidebar-divider)',
            }}
          />

          {/* Chapter list — styled like a book's table of contents */}
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 1.5,
              pb: 4,
              direction: isRtl ? 'ltr' : undefined,
              scrollbarColor: 'var(--reader-scrollbar-main) var(--reader-scrollbar-track)',
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-track': { backgroundColor: 'var(--reader-scrollbar-track)' },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'var(--reader-scrollbar-main)',
                borderRadius: 2,
                border: 'none',
              },
            }}
          >
            <Box sx={{ direction: isRtl ? 'rtl' : undefined }}>
              {readerPinnedTocGroups.length > 0 && (
                <Box sx={{ mb: readerSidebarPinsOnly ? 0 : 2 }}>
                  {readerPinnedTocGroups.map((group, gi) => {
                    const isEmpty = group.rows.length === 0
                    const chapterLabel = formatChapterLabel(gi + 1)
                    const chapterDefId = group.chapterDef?.id
                    return (
                      <Box key={group.chapterDef?.id ?? `group-${gi}`} sx={{ mb: 0.5 }}>
                        {!isFlat && (
                          <Typography
                            onClick={chapterDefId ? () => handleChapterHeaderClick(chapterDefId) : undefined}
                            sx={{
                              fontFamily: bookTitleFont,
                              fontWeight: 700,
                              fontSize: '13px',
                              color: 'var(--reader-text-primary)',
                              px: 1,
                              pt: gi > 0 ? 1.5 : 0,
                              pb: 0.25,
                              opacity: isEmpty ? 0.35 : 1,
                              cursor: chapterDefId ? 'pointer' : 'default',
                              borderRadius: 1,
                              '&:hover': chapterDefId ? { bgcolor: 'var(--reader-hover)' } : undefined,
                            }}
                          >
                            {chapterLabel}
                          </Typography>
                        )}
                        {group.rows.map((row, i) => {
                          const rowChapterKey = chapterKey(row.chapter)
                          return (
                            <Box
                              key={`${rowChapterKey}-${row.blockId}-${i}`}
                              onClick={() => handlePinnedHeadingClick(rowChapterKey, row.blockId)}
                              sx={{
                                px: 2,
                                py: 0.45,
                                cursor: 'pointer',
                                borderRadius: 1,
                                '&:hover': { bgcolor: 'var(--reader-hover)' },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontFamily: bookTitleFont,
                                  fontSize: '12px',
                                  lineHeight: 1.35,
                                  color: 'var(--reader-text-muted)',
                                }}
                              >
                                {row.displayText}
                              </Typography>
                            </Box>
                          )
                        })}
                      </Box>
                    )
                  })}
                </Box>
              )}

              {!readerSidebarPinsOnly &&
                chaptersInReadingOrder.map((chapter) => {
                  const headings = getChapterHeadings(chapter)
                  const key = chapterKey(chapter)
                  const isActive = activeChapterId === key
                  return (
                    <Box key={key} sx={{ mb: 0.25 }}>
                      <Box
                        onClick={() => handleChapterClick(key)}
                        sx={{
                          px: 2,
                          py: 0.75,
                          cursor: 'pointer',
                          borderRadius: 1,
                          bgcolor: 'transparent',
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          '&:hover': {
                            bgcolor: 'var(--reader-hover)',
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: bookTitleFont,
                            fontWeight: 'normal',
                            color: isActive ? 'var(--reader-heading-active)' : 'var(--reader-heading-inactive)',
                            fontSize: '13.5px',
                            lineHeight: 1.5,
                            transition: 'color 0.3s ease',
                            flex: 1,
                          }}
                        >
                          {chapter.title}
                        </Typography>
                      </Box>

                      {/* Sub-headings for active chapter */}
                      {isActive && headings.length > 0 && (
                        <Box sx={{ pb: 0.5 }}>
                          {headings.map((heading, index) => (
                            <Box
                              key={index}
                              sx={{
                                px: 3,
                                py: 0.25,
                                cursor: 'pointer',
                                mx: 1,
                                ml: 2 + (heading.level - 1),
                                '&:hover': {
                                  bgcolor: 'var(--reader-hover-sub)',
                                },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontFamily: bookTitleFont,
                                  color: 'var(--reader-text-submuted)',
                                  opacity: heading.isTocSection ? 0.72 : 1,
                                  fontSize: '12.5px',
                                  lineHeight: 1.4,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {heading.displayText}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )
                })}

              {/* Back matter: an entry in the contents, pointing at the appendix. */}
              {characters.length > 0 && (
                <Box
                  onClick={scrollToCharacters}
                  sx={{
                    display: 'block',
                    mt: 2,
                    mx: 1,
                    px: 1,
                    py: 0.75,
                    borderRadius: 1,
                    cursor: 'pointer',
                    borderTop: '1px solid var(--reader-sidebar-divider)',
                    '&:hover': { bgcolor: 'var(--reader-hover)' },
                  }}
                >
                  <Typography
                    sx={{ fontFamily: bookTitleFont, color: 'var(--reader-text-submuted)', fontSize: '13px' }}
                  >
                    דמויות
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* ═══════════════ Main Content — The Book ═══════════════ */}
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            overflowY: 'auto',
            minWidth: 0,
            direction: isRtl ? 'ltr' : undefined,
            // Smooth scrolling
            scrollBehavior: 'smooth',
            // Paper texture — anchored to scrolling content so the page
            // moves with the text (words sit on the page, not over it).
            ...READER_PAPER_BACKGROUND_SX,
            // Custom scrollbar
            scrollbarColor: 'var(--reader-scrollbar-main) var(--reader-scrollbar-track)',
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'var(--reader-scrollbar-track)' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'var(--reader-scrollbar-main)',
              borderRadius: 3,
              border: 'none',
            },
          }}
        >
          <Box
            sx={{
              maxWidth: '720px',
              width: '100%',
              mx: 'auto',
              px: { xs: 0, md: 2 },
              direction: isRtl ? 'rtl' : undefined,
            }}
          >
            {/* ── Title Page ── */}
            {book && (
              <Box
                sx={{
                  textAlign: 'center',
                  px: 4,
                  height: '100dvh',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  pb: '10vh',
                }}
              >
                {/* Placeholder for title page illustration */}
                <Box
                  sx={{
                    width: { xs: 180, md: 240 },
                    height: { xs: 180, md: 240 },
                    mb: 5,
                    borderRadius: '50%',
                    background: 'radial-gradient(ellipse at center, var(--reader-star-glow) 0%, transparent 70%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <StarCluster size={60} />
                </Box>

                {/* Book Title */}
                <Typography
                  sx={{
                    fontFamily: bookTitleFont,
                    fontWeight: 'normal',
                    fontSize: { xs: '2rem', md: '2.6rem' },
                    color: 'var(--reader-text-primary)',
                    lineHeight: 1.4,
                    letterSpacing: '0.04em',
                    mb: 3,
                  }}
                >
                  {book.title}
                </Typography>

                {/* Ornament below title */}
                <ChapterOrnament />

                {/* Author name */}
                {book.author?.username && (
                  <Typography
                    sx={{
                      fontFamily: bookFont,
                      fontSize: '1rem',
                      color: 'var(--reader-text-submuted)',
                      mt: 4,
                      letterSpacing: '0.06em',
                    }}
                  >
                    by {book.author.username}
                  </Typography>
                )}
              </Box>
            )}

            {/* ── Chapters / Sections ── */}
            {isFlat
              ? (() => {
                  // Flat structure: render each published section in flatSectionsOrder
                  // (the server-sorted `chaptersInReadingOrder`). No chapter labels,
                  // no per-chapter placeholders — just the continuous sequence with
                  // a FlatGapEllipsis inserted wherever the server flagged a gap.
                  if (chaptersInReadingOrder.length === 0) {
                    return (
                      <Box
                        sx={{
                          py: 12,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 3,
                        }}
                      >
                        <ChapterOrnament />
                        <StarCluster size={50} />
                      </Box>
                    )
                  }
                  return chaptersInReadingOrder.map((chapter, idx) => {
                    const key = chapterKey(chapter)
                    const content = reorderedContentByKey?.get(key) ?? chapter.content
                    if (!content) {
                      return null
                    }
                    const sectionTitle = chapter.title?.trim()
                      ? formatChapterSectionHeadingForSidebarDisplay(chapter.title.trim()).trim() ||
                        chapter.title.trim()
                      : ''
                    const gapBefore = Boolean(chapter.hasGapBefore)
                    return (
                      <React.Fragment key={key}>
                        {gapBefore ? <FlatGapMeadow /> : idx > 0 && <SectionOrnament />}
                        <Box
                          id={`chapter-${key}`}
                          sx={{
                            scrollMarginTop: '80px',
                          }}
                        >
                          {sectionTitle && (
                            <Typography
                              sx={{
                                fontFamily: bookTitleFont,
                                fontSize: { xs: '1.5rem', md: '1.4rem' },
                                fontWeight: 500,
                                color: 'var(--reader-text-secondary)',
                                letterSpacing: '0.04em',
                                opacity: 0.85,
                                textAlign: 'center',
                                mb: { xs: 2, md: 3 },
                              }}
                            >
                              {sectionTitle}
                            </Typography>
                          )}
                          <LexicalEditor
                            key={key}
                            bookId={book?._id}
                            initialContent={content}
                            readOnly={true}
                            readerView={true}
                            showToolbar={false}
                            placeholder=""
                            onEncryptedClick={handleEncryptedClick}
                            mnemonicMap={mnemonicMap}
                            glyphMap={glyphMap}
                            sealedIds={sealedIds}
                            onCharacterConsent={handleConsentRequest}
                            isAuthor={isAuthor}
                            bookSettings={book?.settings}
                            style={{
                              minHeight: '100%',
                              border: 'none',
                            }}
                          />
                        </Box>
                      </React.Fragment>
                    )
                  })
                })()
              : (() => {
                  const chapterDefs = (book?.settings?.chapters as Chapter[] | undefined) ?? []
                  if (chapterDefs.length === 0) {
                    return (
                      <Box
                        sx={{
                          py: 12,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 3,
                        }}
                      >
                        <ChapterOrnament />
                        <StarCluster size={50} />
                      </Box>
                    )
                  }
                  return chapterDefs.map((chapterDef, chapterDefIdx) => {
                    const groupChapters = chaptersInReadingOrder.filter((ch) => ch.chapterId === chapterDef.id)
                    const hasContent = groupChapters.length > 0
                    const chapterNumber = formatChapterLabel(chapterDefIdx + 1)
                    return (
                      <React.Fragment key={chapterDef.id}>
                        {hasContent && (
                          <Box
                            id={`chapter-def-${chapterDef.id}`}
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              py: { xs: 0, md: 0 },
                              mb: { xs: 4, md: 5 },
                              scrollMarginTop: '80px',
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily: bookTitleFont,
                                fontSize: { xs: '1.8rem', md: '2rem' },
                                fontWeight: 500,
                                color: 'var(--reader-text-secondary)',
                                letterSpacing: '0.05em',
                                opacity: 1,
                              }}
                            >
                              {chapterNumber}
                            </Typography>
                          </Box>
                        )}

                        {hasContent ? (
                          groupChapters.map((chapter, idx) => {
                            const key = chapterKey(chapter)
                            const content = reorderedContentByKey?.get(key) ?? chapter.content
                            if (!content) {
                              return null
                            }
                            const sectionTitle = chapter.title?.trim()
                              ? formatChapterSectionHeadingForSidebarDisplay(chapter.title.trim()).trim() ||
                                chapter.title.trim()
                              : ''
                            return (
                              <React.Fragment key={key}>
                                {idx > 0 && <SectionOrnament />}
                                <Box
                                  id={`chapter-${key}`}
                                  sx={{
                                    scrollMarginTop: '80px',
                                  }}
                                >
                                  {sectionTitle && (
                                    <Typography
                                      sx={{
                                        fontFamily: bookTitleFont,
                                        fontSize: { xs: '1.5rem', md: '1.4rem' },
                                        fontWeight: 500,
                                        color: 'var(--reader-text-secondary)',
                                        letterSpacing: '0.04em',
                                        opacity: 0.85,
                                        textAlign: 'center',
                                        mb: { xs: 2, md: 3 },
                                      }}
                                    >
                                      {sectionTitle}
                                    </Typography>
                                  )}
                                  <LexicalEditor
                                    key={key}
                                    bookId={book?._id}
                                    initialContent={content}
                                    readOnly={true}
                                    readerView={true}
                                    showToolbar={false}
                                    placeholder=""
                                    onEncryptedClick={handleEncryptedClick}
                                    mnemonicMap={mnemonicMap}
                                    glyphMap={glyphMap}
                                    sealedIds={sealedIds}
                                    onCharacterConsent={handleConsentRequest}
                                    isAuthor={isAuthor}
                                    bookSettings={book?.settings}
                                    style={{
                                      minHeight: '100%',
                                      border: 'none',
                                    }}
                                  />
                                </Box>
                              </React.Fragment>
                            )
                          })
                        ) : (
                          <ChapterPlaceholder
                            id={`chapter-def-${chapterDef.id}`}
                            titleFont={bookTitleFont}
                            chapterNumber={chapterNumber}
                          />
                        )}
                      </React.Fragment>
                    )
                  })
                })()}

            {/* ── End of book ── */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: { xs: 8, md: 12 },
                gap: 2,
              }}
            >
              <ChapterOrnament />
              <StarCluster size={30} />
            </Box>

            {/* ── Back matter: the characters, where an appendix sits in a book ── */}
            {characters.length > 0 && book && (
              <Box id="characters-appendix" sx={{ scrollMarginTop: '80px', pb: { xs: 10, md: 16 } }}>
                <Typography
                  sx={{
                    fontFamily: bookTitleFont,
                    fontSize: { xs: '1.5rem', md: '1.4rem' },
                    fontWeight: 500,
                    color: 'var(--reader-text-secondary)',
                    letterSpacing: '0.04em',
                    textAlign: 'center',
                    mb: { xs: 3, md: 4 },
                  }}
                >
                  דמויות
                </Typography>
                <Stack spacing={0.5} alignItems="center">
                  {characters.map((c) => (
                    <Box
                      key={c.tagId}
                      component={Link}
                      to={`/character/${book._id}/${c.tagId}`}
                      sx={{
                        display: 'block',
                        px: 3,
                        py: 1,
                        borderRadius: '6px',
                        textDecoration: 'none',
                        transition: 'background-color 0.2s ease',
                        '&:hover': { backgroundColor: 'var(--reader-hover)' },
                      }}
                    >
                      <Typography
                        sx={{ fontFamily: bookTitleFont, fontSize: '1.15rem', color: 'var(--reader-text-primary)' }}
                      >
                        {c.name}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Box>
        </Box>

        {/* Decryption Modal */}
        <DecryptionModal
          open={decryptionModalOpen}
          onClose={() => setDecryptionModalOpen(false)}
          encryptedContent={encryptedContentToDecrypt}
          onDecryptSuccess={handleDecryptSuccess}
          direction={isRtl ? 'rtl' : 'ltr'}
          titleFontFamily={book?.settings.titleFontFamily}
        />

        <CharacterConsentMenu
          anchorEl={consentTarget?.anchor ?? null}
          onClose={() => setConsentTarget(null)}
          onChoose={handleCharacterConsent}
          titleFontFamily={book?.settings.titleFontFamily}
        />
      </Box>

      {/* Mobile TOC bottom sheet — outside overflow:hidden container */}
      {readerSidebarPinsOnly && (
        <>
          <Fab
            size="small"
            onClick={() => setTocOpen(true)}
            sx={{
              display: { xs: 'flex', md: 'none' },
              position: 'fixed',
              bottom: 24,
              left: 24,
              bgcolor: 'var(--reader-page-bg)',
              color: 'var(--reader-text-secondary)',
              border: '1px solid var(--reader-text-submuted)',
              boxShadow: 2,
              '&:hover': { bgcolor: 'var(--reader-hover)' },
              zIndex: 10,
            }}
          >
            <ListAlt fontSize="small" />
          </Fab>
          <SwipeableDrawer
            anchor="bottom"
            open={tocOpen}
            onOpen={() => setTocOpen(true)}
            onClose={() => setTocOpen(false)}
            PaperProps={{
              dir: isRtl ? 'rtl' : 'ltr',
              sx: {
                maxHeight: '70vh',
                borderRadius: '16px 16px 0 0',
                color: 'var(--reader-text)',
                // The inner scrolling Box (next sibling) fills the drawer and
                // owns the textured paper. `overflow: hidden` clips the inner
                // Box to the rounded top corners.
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              },
            }}
          >
            {/* TOC content + sticky drag handle on textured paper */}
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                // Paper texture lives on the scrolling element so it moves
                // with the TOC text on scroll, like the main reading area.
                ...READER_PAPER_BACKGROUND_SX,
                px: 2,
                pb: 3,
              }}
            >
              {/* Drag handle — sticky so it floats on the paper while the
                  TOC text scrolls underneath it. No background, so the paper
                  shows through around the small gray pill. */}
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  py: 1,
                }}
              >
                <Box
                  sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: 'var(--reader-text-submuted)', opacity: 0.5 }}
                />
              </Box>
              {readerPinnedTocGroups.map((group, gi) => {
                const isEmpty = group.rows.length === 0
                const chapterLabel = formatChapterLabel(gi + 1)
                const chapterDefId = group.chapterDef?.id
                return (
                  <Box key={group.chapterDef?.id ?? `group-${gi}`} sx={{ mb: 0.5 }}>
                    {!isFlat && (
                      <Typography
                        onClick={
                          chapterDefId
                            ? () => {
                                handleChapterHeaderClick(chapterDefId)
                                setTocOpen(false)
                              }
                            : undefined
                        }
                        sx={{
                          fontFamily: bookTitleFont,
                          fontWeight: 700,
                          fontSize: '15px',
                          color: 'var(--reader-text-primary)',
                          px: 1,
                          pt: gi > 0 ? 2 : 0.5,
                          pb: 0.5,
                          opacity: isEmpty ? 0.35 : 1,
                          cursor: chapterDefId ? 'pointer' : 'default',
                          borderRadius: 1,
                          '&:hover': chapterDefId ? { bgcolor: 'var(--reader-hover)' } : undefined,
                        }}
                      >
                        {chapterLabel}
                      </Typography>
                    )}
                    {group.rows.map((row, i) => {
                      const rowChapterKey = chapterKey(row.chapter)
                      return (
                        <Box
                          key={`${rowChapterKey}-${row.blockId}-${i}`}
                          onClick={() => {
                            handlePinnedHeadingClick(rowChapterKey, row.blockId)
                            setTocOpen(false)
                          }}
                          sx={{
                            px: 2.5,
                            py: 0.5,
                            cursor: 'pointer',
                            borderRadius: 1,
                            '&:hover': { bgcolor: 'var(--reader-hover)' },
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: bookTitleFont,
                              fontSize: '13px',
                              lineHeight: 1.4,
                              color: 'var(--reader-text-muted)',
                            }}
                          >
                            {row.displayText}
                          </Typography>
                        </Box>
                      )
                    })}
                  </Box>
                )
              })}

              {/* Back matter, same as the desktop contents — the mobile sheet is the
                  only table of contents a phone ever sees. */}
              {characters.length > 0 && (
                <Box
                  onClick={() => {
                    scrollToCharacters()
                    setTocOpen(false)
                  }}
                  sx={{
                    display: 'block',
                    mt: 2,
                    mx: 1,
                    px: 1.5,
                    py: 1,
                    borderRadius: 1,
                    cursor: 'pointer',
                    borderTop: '1px solid var(--reader-sidebar-divider)',
                    '&:hover': { bgcolor: 'var(--reader-hover)' },
                  }}
                >
                  <Typography sx={{ fontFamily: bookTitleFont, color: 'var(--reader-text-muted)', fontSize: '14px' }}>
                    דמויות
                  </Typography>
                </Box>
              )}
            </Box>
          </SwipeableDrawer>
        </>
      )}
    </>
  )
}

export default TheLastManifesto
